import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { GoogleLogo, Gift } from "@phosphor-icons/react";
import SpecialtyCombobox from "@/components/SpecialtyCombobox";

export default function Signup() {
  const { signup } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const refFromUrl = (params.get("ref") || "").toUpperCase();
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", password: "", specialty: "", referral_code: refFromUrl });
  const [busy, setBusy] = useState(false);
  const [showReferral, setShowReferral] = useState(!!refFromUrl);

  useEffect(() => {
    if (refFromUrl && !form.referral_code) setForm((f) => ({ ...f, referral_code: refFromUrl }));
  }, [refFromUrl]); // eslint-disable-line

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        name: `${form.first_name.trim()} ${form.last_name.trim()}`,
        referral_code: form.referral_code?.trim() || undefined,
      };
      await signup(payload);
      toast.success(form.referral_code ? "Bienvenue ! Vous et votre parrain gagnez 1 mois offert dès votre 1ère souscription." : "Bienvenue sur Questiomed !");
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Inscription impossible");
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#F4F7FB]">
      <div className="flex items-center justify-center p-8 lg:p-16 order-2 lg:order-1">
        <form onSubmit={onSubmit} className="w-full max-w-md space-y-6">
          <div>
            <Link to="/" className="flex items-center gap-2 mb-8 lg:hidden">
              <span className="w-8 h-8 rounded-[10px] bg-[#3A8FA5] flex items-center justify-center text-white font-bold text-base">Q</span>
              <span className="text-[18px] font-bold tracking-tight text-[#1E3A5F]">Questiomed</span>
            </Link>
            <h1 className="text-4xl font-bold tracking-tight text-[#1E3A5F] mb-2">Créer votre compte</h1>
            <p className="text-[#5F758F]">30 secondes suffisent.</p>
          </div>

          {refFromUrl && (
            <div className="bg-[#E6F1F5] border border-[#C7DEE6] rounded-2xl p-4 flex items-start gap-3" data-testid="signup-referral-banner">
              <Gift size={20} weight="duotone" className="text-[#3A8FA5] shrink-0 mt-0.5" />
              <div className="text-sm text-[#1E3A5F]">
                <p className="font-semibold">Vous êtes parrainé(e) <span className="tabular-nums">{refFromUrl}</span> !</p>
                <p className="text-[#5F758F] mt-0.5"><strong>1 mois offert</strong> dès votre première souscription à un abonnement payant. Votre parrain aussi.</p>
              </div>
            </div>
          )}

          <Button type="button" onClick={googleLogin} variant="outline"
            className="w-full h-12 rounded-[10px] border-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white font-semibold flex items-center gap-3"
            data-testid="google-signup-btn">
            <GoogleLogo size={20} weight="bold" /> Continuer avec Google
          </Button>

          <div className="flex items-center gap-4">
            <div className="flex-1 h-px bg-[#E1E9F0]" />
            <span className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">ou</span>
            <div className="flex-1 h-px bg-[#E1E9F0]" />
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Prénom</Label>
                <Input required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  className="mt-2 h-12 bg-white border-[#E1E9F0] rounded-[10px] focus-visible:ring-[#3A8FA5]"
                  data-testid="signup-first-name" />
              </div>
              <div>
                <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Nom</Label>
                <Input required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  className="mt-2 h-12 bg-white border-[#E1E9F0] rounded-[10px] focus-visible:ring-[#3A8FA5]"
                  data-testid="signup-last-name" />
              </div>
            </div>
            <div>
              <SpecialtyCombobox
                value={form.specialty}
                onChange={(v) => setForm({ ...form, specialty: v })}
                testid="signup-specialty"
              />
            </div>
            <div>
              <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Email</Label>
              <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-2 h-12 bg-white border-[#E1E9F0] rounded-[10px] focus-visible:ring-[#3A8FA5]"
                data-testid="signup-email" />
            </div>
            <div>
              <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Mot de passe</Label>
              <Input type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="mt-2 h-12 bg-white border-[#E1E9F0] rounded-[10px] focus-visible:ring-[#3A8FA5]"
                data-testid="signup-password" />
            </div>
            {!refFromUrl && (
              <div>
                {showReferral ? (
                  <>
                    <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold flex items-center gap-2">
                      <Gift size={14} weight="duotone" className="text-[#3A8FA5]" />
                      Code de parrainage <span className="normal-case tracking-normal text-[#5F758F]">(optionnel)</span>
                    </Label>
                    <Input placeholder="ex. A1B2C3D4"
                      value={form.referral_code}
                      onChange={(e) => setForm({ ...form, referral_code: e.target.value.toUpperCase() })}
                      className="mt-2 h-12 bg-white border-[#E1E9F0] rounded-[10px] focus-visible:ring-[#3A8FA5] tracking-widest uppercase font-semibold"
                      maxLength={8}
                      data-testid="signup-referral" />
                    <p className="text-[12px] text-[#5F758F] mt-1.5">Vous et votre parrain gagnez <strong className="text-[#3A8FA5]">1 mois offert</strong> dès votre première souscription à un abonnement payant.</p>
                  </>
                ) : (
                  <button type="button" onClick={() => setShowReferral(true)} className="text-[13px] text-[#3A8FA5] hover:text-[#164E5F] font-semibold inline-flex items-center gap-1" data-testid="signup-show-referral">
                    <Gift size={14} weight="duotone" /> J'ai un code de parrainage
                  </button>
                )}
              </div>
            )}
          </div>

          <Button type="submit" disabled={busy} className="w-full h-12 rounded-[10px] bg-[#3A8FA5] hover:bg-[#164E5F] text-white font-semibold"
            data-testid="signup-submit">
            {busy ? "Création…" : "Créer mon compte"}
          </Button>

          <p className="text-sm text-center text-[#5F758F]">
            Déjà un compte ? <Link to="/login" className="text-[#1E3A5F] underline underline-offset-4" data-testid="link-login">Se connecter</Link>
          </p>
        </form>
      </div>

      <div className="hidden lg:flex flex-col justify-between p-16 bg-[#1E3A5F] text-white relative overflow-hidden order-1 lg:order-2">
        <Link to="/" className="flex items-center gap-2 relative z-10">
          <span className="w-8 h-8 rounded-[10px] bg-[#3A8FA5] flex items-center justify-center text-white font-bold text-base">Q</span>
          <span className="text-[18px] font-bold tracking-tight">Questiomed</span>
        </Link>
        <div className="relative z-10">
          <p className="text-4xl font-bold tracking-tight leading-tight">
            Vos questionnaires patients en ligne.<br/>La consultation simplifiée.
          </p>
        </div>
        <div className="absolute inset-0 opacity-30" style={{ background: "radial-gradient(ellipse at top left, #3A8FA5, transparent 50%)" }} />
      </div>
    </div>
  );
}
